import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { TimeRangeFilter } from './Policy/_types';
import { getDateRangeFromFilter } from './Policy/_constants';

export interface QueryParams {
  startDate: string;
  endDate: string;
}

interface RiskPoolOrgContextValue {
  orgId: string;
  queryParams: QueryParams;
  timeRangeFilter: TimeRangeFilter;
  setTimeRangeFilter: (filter: TimeRangeFilter) => void;
}

export const RiskPoolOrgContext = createContext<RiskPoolOrgContextValue | undefined>(undefined);

interface RiskPoolOrgProviderProps {
  children: ReactNode;
  orgId: string;
  initialStartDate?: string;
  initialEndDate?: string;
  initialTimeRangeFilter?: TimeRangeFilter;
}

export function RiskPoolOrgProvider({
  children,
  orgId,
  initialStartDate,
  initialEndDate,
  initialTimeRangeFilter = TimeRangeFilter.PAST_12_MONTHS,
}: RiskPoolOrgProviderProps) {
  const [timeRangeFilter, setTimeRangeFilter] = useState<TimeRangeFilter>(initialTimeRangeFilter);
  const [customDates, setCustomDates] = useState<{ startDate?: string; endDate?: string }>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });

  // Sync URL params with state when they change
  useEffect(() => {
    setCustomDates({
      startDate: initialStartDate,
      endDate: initialEndDate,
    });
  }, [initialStartDate, initialEndDate]);

  // Sync time range filter when it changes from URL
  useEffect(() => {
    setTimeRangeFilter(initialTimeRangeFilter);
  }, [initialTimeRangeFilter]);

  // Get dates from custom dates or calculate from filter
  const { startDate, endDate } =
    customDates.startDate && customDates.endDate
      ? { startDate: customDates.startDate, endDate: customDates.endDate }
      : getDateRangeFromFilter(timeRangeFilter);

  const queryParams: QueryParams = {
    startDate,
    endDate,
  };

  return (
    <RiskPoolOrgContext.Provider
      value={{
        orgId,
        queryParams,
        timeRangeFilter,
        setTimeRangeFilter,
      }}
    >
      {children}
    </RiskPoolOrgContext.Provider>
  );
}

export function useRiskPoolOrgContext(): RiskPoolOrgContextValue {
  const context = useContext(RiskPoolOrgContext);
  if (context === undefined) {
    throw new Error('useRiskPoolOrgContext must be used within a RiskPoolOrgProvider');
  }
  return context;
}
